import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {OrderItem} from "../../../@services/gen/model/orderItem";
import {
  GetPagedOrderQuery,
  OrderControllerService, OrderItemReq, OrderReq,
  TableMerchant,
  TableMerchantControllerService
} from "../../../@services/gen";
import * as dayjs from "dayjs";
import {UserShiftService} from "../../../@services/user-shift.service";
import {Order} from "../../../@services/gen/model/order";
import OrderStatusEnum = OrderItemReq.OrderStatusEnum;
import {Subject} from "rxjs";
import {
  calculateSplitBillNetAmount, 
  roundCurrencyForSplitBill, 
  createSplitBillItemKey,
  calculateRemainingItems,
  distributeSplitBillComponent,
  validateSplitBillTotals
} from "../../../@services/split-bill-utils";

export interface ExtendedOrderItems extends OrderItemReq {
  selected: boolean;
  tempId: string;
  tempQty: number;
  order: OrderReq;
}

export interface ExtendedOrder extends OrderReq {
  toBePaidTotal: number;
  orderItems: ExtendedOrderItems[];
}

@Component({
  selector: "app-pos-split-bill-dialog",
  templateUrl: "./pos-split-bill-dialog.component.html",
  styleUrls: ["./pos-split-bill-dialog.component.scss"],
})
export class PosSplitBillDialogComponent {
  tableMerchant: TableMerchant;
  merchantId: number;
  unpaidOrders: ExtendedOrder [];
  selectedRows: boolean[] = [];
  loadUnpaidOrders$: Subject<OrderReq[]> = new Subject<OrderReq[]>();

  constructor(private dialogRef: MatDialogRef<PosSplitBillDialogComponent>, @Inject(MAT_DIALOG_DATA) private data: any, private orderService: OrderControllerService, private customUserShiftService: UserShiftService) {
    this.tableMerchant = data.tableMerchant;
    this.merchantId = data.merchantId;
    this.loadUnpaidOrders(data.tableMerchant);
  }

  /**
   * Refresh data - useful for debugging or manual refresh
   */
  refreshData() {
    console.log('Refreshing split bill data...');
    this.clearQuantityCache();
    this.loadUnpaidOrders(this.tableMerchant);
  }

  private loadUnpaidOrders(table: TableMerchant) {
    let shiftStartDate = this.customUserShiftService.getUserShift().start;
    this.orderService
      .getPagedOrderPost({
        merchantId: this.merchantId,
        tableId: table.id,
        orderStatusEnum: [GetPagedOrderQuery.OrderStatusEnumEnum.Unpaid, GetPagedOrderQuery.OrderStatusEnumEnum.PartiallyPaid],
        orderItemStatusEnum: [GetPagedOrderQuery.OrderStatusEnumEnum.Unpaid, GetPagedOrderQuery.OrderStatusEnumEnum.PartiallyPaid,],
        startDate: shiftStartDate,
        endDate: dayjs(new Date()).endOf('day').toString(),
        pageRequest: {
          page: 0,
          size: 9999,
          orders: [{direction: 'DESC', property: 'creationTime'}],
        },
      })
      .subscribe((res) => {

        res.content.forEach((item)=> {
          console.log(item);
        });
        this.unpaidOrders = (res.content as OrderReq[]).map(order => ({
          ...order,
          toBePaidTotal: 0, // Initialize toBePaidTotal to satisfy ExtendedOrder type
          orderItems: order.orderItems.flatMap(item => {
            const remainingQuantity = item.quantity - item.paidQuantity;

            // Calculate per-unit amounts to avoid carrying full amounts in virtual items
            const perUnitSubtotal = item.subtotal / item.quantity;
            const perUnitNetAmount = item.netAmount / item.quantity;
            const perUnitDiscountAmount = (item.discountAmount || 0) / item.quantity;
            const perUnitTax1Amount = (item.tax1Amount || 0) / item.quantity;
            const perUnitTax2Amount = (item.tax2Amount || 0) / item.quantity;
            const perUnitInclusiveTax = (item.inclusiveTax || 0) / item.quantity;
            const perUnitRoundingAmount = (item.roundingAmount || 0) / item.quantity;

            return Array.from({length: remainingQuantity}, (_, quantityIndex) => ({
              // Copy metadata fields only (not calculated amount fields)
              id: item.id,
              name: item.name,
              price: item.price,
              skuNo: item.skuNo,
              instructions: item.instructions,
              variance: item.variance,
              varianceModel: item.varianceModel,
              itemCategory: item.itemCategory,
              posItemVsMerchant: item.posItemVsMerchant,
              kitchenStatus: item.kitchenStatus,
              orderStatus: item.orderStatus,
              orderType: item.orderType,
              prepareType: item.prepareType,

              // Set normalized quantities (each virtual item represents 1 unit)
              quantity: 1,
              paidQuantity: 0,
              toBePaidQuantity: 1,

              // Set per-unit amounts (not full amounts)
              subtotal: perUnitSubtotal,
              netAmount: perUnitNetAmount,
              discountAmount: perUnitDiscountAmount,
              tax1Amount: perUnitTax1Amount,
              tax2Amount: perUnitTax2Amount,
              inclusiveTax: perUnitInclusiveTax,
              roundingAmount: perUnitRoundingAmount,
              paidAmount: 0,

              // Split bill UI fields
              order: {id: order.id},
              tempQty: 1,
              selected: false,
              // Fix: tempId should continue from where paid quantities left off
              tempId: `${item.id}_${item.paidQuantity + quantityIndex + 1}`
            }));
          })
        })) as ExtendedOrder[];

        this.loadUnpaidOrders$.next(this.unpaidOrders);
      });
  }

  toggleItemSelection(orderIndex: number, itemIndex: number) {
    this.unpaidOrders[orderIndex].orderItems[itemIndex].selected = !this.unpaidOrders[orderIndex].orderItems[itemIndex].selected;
    // Clear cache when selection changes
    this.clearQuantityCache();
  }

  isItemSelected(orderIndex: number, itemIndex: number): boolean {
    return this.unpaidOrders[orderIndex].orderItems[itemIndex].selected;
  }

  toggleRowSelection(orderIndex: number) {
    this.unpaidOrders[orderIndex].orderItems.forEach(item => {
      item.selected = !item.selected;
    });
    // Clear cache when selection changes
    this.clearQuantityCache();
  }

  getOrdersWithSelectedItems(): ExtendedOrder[] {
    const ordersWithSelectedItems: ExtendedOrder[] = [];

    this.unpaidOrders.forEach(order => {
      const selectedItems = order.orderItems.filter(item => item.selected);
      if (selectedItems.length > 0) {
        ordersWithSelectedItems.push({ ...order, orderItems: selectedItems });
      }
    });

    return ordersWithSelectedItems;
  }

  /**
   * Get remaining quantity for display by aggregating virtual items back to original quantities
   * @param orderIndex - Index of the order
   * @param itemId - Original item ID to check
   * @returns Object with original quantity, paid quantity, selected count, and remaining count
   */
  getRemainingQuantityForDisplay(orderIndex: number, itemId: number): {
    originalQuantity: number;
    paidQuantity: number;
    selectedCount: number;
    remainingCount: number;
  } {
    const order = this.unpaidOrders[orderIndex];
    if (!order) {
      return { originalQuantity: 0, paidQuantity: 0, selectedCount: 0, remainingCount: 0 };
    }

    // Find the original item data from the first virtual item with this ID
    const firstVirtualItem = order.orderItems.find(item => item.id === itemId);
    if (!firstVirtualItem) {
      return { originalQuantity: 0, paidQuantity: 0, selectedCount: 0, remainingCount: 0 };
    }

    // Count selected virtual items for this item ID
    const virtualItemsForThisId = order.orderItems.filter(item => item.id === itemId);
    const selectedCount = virtualItemsForThisId.filter(item => item.selected).length;

    const originalQuantity = firstVirtualItem.quantity;
    const paidQuantity = firstVirtualItem.paidQuantity;
    const remainingCount = originalQuantity - paidQuantity - selectedCount;

    return {
      originalQuantity,
      paidQuantity,
      selectedCount,
      remainingCount: Math.max(0, remainingCount)
    };
  }

  /**
   * Check if all virtual items for a given original item are selected
   * @param orderIndex - Index of the order
   * @param itemId - Original item ID to check
   * @returns true if all virtual items are selected
   */
  areAllVirtualItemsSelected(orderIndex: number, itemId: number): boolean {
    const order = this.unpaidOrders[orderIndex];
    if (!order) return false;

    const virtualItemsForThisId = order.orderItems.filter(item => item.id === itemId);
    return virtualItemsForThisId.length > 0 && virtualItemsForThisId.every(item => item.selected);
  }

  /**
   * Memoized version of getRemainingQuantityForDisplay to avoid multiple calculations in template
   */
  private quantityDisplayCache = new Map<string, any>();

  getQuantityDisplayInfo(orderIndex: number, itemId: number): {
    originalQuantity: number;
    paidQuantity: number;
    selectedCount: number;
    remainingCount: number;
  } {
    const cacheKey = `${orderIndex}-${itemId}`;
    
    // Check cache first
    if (this.quantityDisplayCache.has(cacheKey)) {
      return this.quantityDisplayCache.get(cacheKey);
    }

    const result = this.getRemainingQuantityForDisplay(orderIndex, itemId);
    this.quantityDisplayCache.set(cacheKey, result);
    return result;
  }

  /**
   * Clear cache when selections change
   */
  private clearQuantityCache(): void {
    this.quantityDisplayCache.clear();
  }

  submit() {
    try {
      const ordersWithSelectedItems = this.getOrdersWithSelectedItems();
      
      // Validate that items are selected
      if (ordersWithSelectedItems.length === 0) {
        console.warn('No items selected for split bill');
        return;
      }

      // Use composite key to properly combine items across orders
      const combinedItemsMap = new Map<string, ExtendedOrderItems>();

      ordersWithSelectedItems.forEach(order => {
        order.orderItems.forEach(orderItem => {
          if (orderItem.selected) {
            // Create composite key using order ID and item ID
            const itemKey = createSplitBillItemKey(order.id, orderItem.id);
            const existingItem = combinedItemsMap.get(itemKey);
            
            if (existingItem) {
              // Combine quantities for same item
              existingItem.toBePaidQuantity = (existingItem.toBePaidQuantity || 0) + 1;
            } else {
              // Create new combined item
              const combinedItem: ExtendedOrderItems = {
                ...orderItem,
                toBePaidQuantity: 1, // Start with 1 for this selection
                paidQuantity: orderItem.paidQuantity, // Keep original paidQuantity
                selected: true,
                order: {id: order.id}
              };
              
              combinedItemsMap.set(itemKey, combinedItem);
            }
          }
        });
      });

      // Convert map values back to an array of items
      const combinedItems = Array.from(combinedItemsMap.values());

      // Process each order to update items and calculate totals
      const processedOrders = ordersWithSelectedItems.map(order => {
        // Filter items that belong to this order
        const orderCombinedItems = combinedItems.filter(item => 
          item.order.id === order.id
        );

        // Calculate amounts for combined items
        // Since Fix #1 already normalized items to per-unit amounts (quantity=1),
        // we simply multiply by the combined toBePaidQuantity
        orderCombinedItems.forEach(orderItem => {
          const splitQuantity = orderItem.toBePaidQuantity;

          // Since orderItem already has per-unit amounts, just multiply by quantity
          orderItem.subtotal = orderItem.subtotal * splitQuantity;

          // Handle optional amount fields
          if (orderItem.discountAmount) {
            orderItem.discountAmount = orderItem.discountAmount * splitQuantity;
          }

          if (orderItem.tax1Amount) {
            orderItem.tax1Amount = orderItem.tax1Amount * splitQuantity;
          }

          if (orderItem.tax2Amount) {
            orderItem.tax2Amount = orderItem.tax2Amount * splitQuantity;
          }

          if (orderItem.inclusiveTax) {
            orderItem.inclusiveTax = orderItem.inclusiveTax * splitQuantity;
          }

          if (orderItem.roundingAmount) {
            orderItem.roundingAmount = orderItem.roundingAmount * splitQuantity;
          }

          // Update quantity to reflect combined split quantity
          orderItem.quantity = splitQuantity;
          // CRITICAL: Also update toBePaidQuantity for order.service.ts calculations
          orderItem.toBePaidQuantity = splitQuantity;
        });

        // Calculate order total
        let paidAmount = 0.0;
        orderCombinedItems.forEach(orderItem => {
          const itemNetAmount = calculateSplitBillNetAmount(
            orderItem.subtotal || 0,
            orderItem.discountAmount || 0,
            (orderItem.tax1Amount || 0) + (orderItem.tax2Amount || 0),
            orderItem.roundingAmount || 0
          );
          paidAmount += itemNetAmount;
        });

        const processedOrder: ExtendedOrder = {
          ...order,
          orderItems: orderCombinedItems,
          toBePaidTotal: roundCurrencyForSplitBill(paidAmount)
        };

        return processedOrder;
      });

      // Note: Parent component will handle data refresh after modal closes

      // Validate split bill totals
      const totalValidation = validateSplitBillTotals(
        processedOrders.map(o => ({amount: o.toBePaidTotal})),
        processedOrders.reduce((sum, order) => sum + order.toBePaidTotal, 0)
      );
      
      if (!totalValidation.isValid) {
        console.warn('Split bill validation failed:', totalValidation);
      }

      console.log('Processed split bill orders:', processedOrders);

      this.dialogRef.close({
        selectedPaidOrder: processedOrders,
      });
      
    } catch (error) {
      console.error('Error processing split bill:', error);
      // Could add user notification here
    }
  }


  checkIfSelectItem(){
    if(this.unpaidOrders){
      for (const order of this.unpaidOrders) {
        for (const item of order.orderItems) {
          if (item.selected) {

            return false;
          }
        }
      }

    }

    return true;
  }
}
